import type { ReactElement } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";

import { AppShell } from "./app/AppShell";
import { NotFoundScreen, RouteErrorScreen } from "./app/RouteFallbacks";
import { LoginRoute } from "./features/auth/LoginRoute";
import { RequireSession } from "./features/auth/RequireSession";
import { CaseRoute } from "./features/cases/CaseRoute";
import { DemoScreen } from "./features/demo/DemoScreen";
import { EingangScreen } from "./features/eingang/EingangScreen";
import { ExtractionScreen } from "./features/extraction/ExtractionScreen";
import { OutlineDraftScreen } from "./features/outline/OutlineDraftScreen";
import { PlanningRoute } from "./features/planning/PlanningRoute";
import { CalculationEntry } from "./features/pricing/CalculationEntry";
import { CalculationRoute } from "./features/pricing/CalculationRoute";
import { ReadingJobScreen } from "./features/reading/ReadingJobScreen";
import { RecordScreen } from "./features/record/RecordScreen";
import { RegisterScreen } from "./features/register/RegisterScreen";
import { CaseSchemaRoute } from "./features/schema/CaseSchemaRoute";
import { SchemaScreen } from "./features/schema/SchemaScreen";
import { StartScreen } from "./features/start/StartScreen";
import { crossFadePageTurns, SHELL_ROUTE } from "./lib/motion";

/**
 * The dev preview of plan 4's screens (`/_vorschau/*`): the review, „Zeichnung
 * lesen“ and the seeded editor on synthetic readings, inside the real shell,
 * without a server. Development builds only, like `/_muster` — the whole
 * branch is a literal `false` in a production build and its lazy imports go
 * with it. The build check greps for its path as for the pattern page's.
 */
const previewRoutes = import.meta.env.DEV
  ? [
      {
        path: "/_vorschau",
        HydrateFallback: () => null,
        lazy: async () => ({ Component: (await import("./app/PreviewSession")).PreviewSession }),
        children: [
          // The print documents on synthetic data — outside the shell, as the
          // real route is: a print preview has no masthead to hide.
          {
            path: "unterlagen",
            HydrateFallback: () => null,
            lazy: async () => ({ Component: (await import("./features/print/PrintPreview")).PrintPreview }),
          },
          {
            element: <AppShell />,
            handle: SHELL_ROUTE,
            children: [
              {
                path: "pruefung",
                HydrateFallback: () => null,
                lazy: async () => ({
                  Component: (await import("./features/schema/review/ReviewPreview")).ReviewPreview,
                }),
              },
              {
                path: "lesen",
                HydrateFallback: () => null,
                lazy: async () => ({ Component: (await import("./features/reading/ReadingPreview")).ReadingPreview }),
              },
              {
                path: "umriss",
                HydrateFallback: () => null,
                lazy: async () => ({ Component: (await import("./features/outline/SeededPreview")).SeededPreview }),
              },
              // The mail's Eingang on synthetic mails (spec 2026-10-20 §3.1).
              {
                path: "eingang",
                HydrateFallback: () => null,
                lazy: async () => ({ Component: (await import("./features/eingang/EingangPreview")).EingangPreview }),
              },
            ],
          },
        ],
      },
    ]
  : [];

/**
 * The register demo's stateless pages (Welle 0, S3, 18.10.2026): example
 * figures and „Gespeichert ist nichts" in the production build K60 works on
 * from 19.10. There they lead to the start page; development keeps them, as
 * it keeps `/_muster`. `/schema` is not one of them: nothing in the product
 * leads there any more — a Vorgang drawn by hand lands on its board, and only
 * the demo pages `/auslese` and `/planung` still go there —, and the screen
 * sends an empty visit home itself. `/kalkulation` already only points the
 * way.
 */
function demoOnly(element: ReactElement): ReactElement {
  return import.meta.env.DEV ? element : <Navigate to="/" replace />;
}

export const routes = [
  ...previewRoutes,
  // The door. Outside the shell on purpose: no masthead, no stage, no health
  // dot — nothing to navigate before there is a session; and outside the
  // guard, because there is no session yet to check.
  { path: "/anmelden", element: <LoginRoute /> },
  {
    // Everything else assumes a session — the shell shows a name, the
    // screens confirm as somebody. One guard, above the shell, rather than
    // one check per screen.
    element: <RequireSession />,
    // A shell that fails itself, or the print sheets: no masthead to keep.
    errorElement: <RouteErrorScreen />,
    children: [
      // The documents of a Stand, as A4 sheets to print or save as PDF
      // (design 2026-10-12, WP-E2-4). Outside the shell: a print preview has
      // no masthead, its own bar says where back is. Loaded on
      // demand — the sheets and their named page are not part of any other
      // screen.
      {
        path: "/vorgang/:caseId/unterlagen",
        HydrateFallback: () => null,
        lazy: async () => ({ Component: (await import("./features/print/PrintRoute")).PrintRoute }),
      },
      {
        path: "/",
        element: <AppShell />,
        // Pages under the shell cross-fade into each other (idea 26,
        // `lib/motion.ts`); the door and the print sheets stand outside it.
        handle: SHELL_ROUTE,
        children: [
          {
            // A screen that fails to render, under the masthead (Welle 0, S4): a
            // pathless route with only an error boundary keeps the shell standing.
            errorElement: <RouteErrorScreen />,
            children: [
              // Begrüßung, zwei gleichrangige Wege hinein, eigene Vorgänge —
              // eine Seite statt Übersicht und Eingang getrennt (design
              // 2026-09-07 §1, C-1). Der frühere Eingangsschirm ist entfallen.
              { index: true, element: <StartScreen /> },
              // Every Vorgang of mine as one table (design 2026-09-22 §8.1). Rows
              // open the record, not the board — the record says where to go next.
              { path: "vorgaenge", element: <RegisterScreen /> },
              { path: "auslese", element: demoOnly(<ExtractionScreen />) },
              // The way in without a drawing: the shape is made by hand and joins the
              // chain at the same place a read one does (design 2026-08-15 §4.3).
              // Nothing was read on this path, so it stands beside the Auslese and
              // not after it: `caseTabs` shows the Umriss tab only where there is no
              // drawing and no Stand, or when it is the page you are on.
              { path: "umriss", element: <OutlineDraftScreen /> },
              { path: "schema", element: <SchemaScreen /> },
              // One Vorgang as a whole: fact sheet and record (design 2026-09-22
              // §8.2). Its own route in front of the per-Vorgang steps below —
              // reading, schema, board, calculation; it leads to whichever comes
              // next. The masthead lights „Vorgänge" on every page of a Vorgang, and
              // the Vorgang's tabs (`CaseFrame`) lead from here to its pages.
              { path: "vorgang/:caseId", element: <RecordScreen /> },
              // Where a customer's mail lands after the start page filed it
              // (spec 2026-10-20 §3.1): who the customer is, which drawings
              // are read. `?mail=<entryId>` names the mail; a reload reads
              // both from the server again.
              { path: "vorgang/:caseId/eingang", element: <EingangScreen /> },
              // A reading the worker has — or had — for this Vorgang (design
              // 2026-09-23 §3.6). The URL is the state, so a reload shows it.
              { path: "vorgang/:caseId/einlesen", element: <ReadingJobScreen /> },
              { path: "vorgang/:caseId/schema", element: <CaseSchemaRoute /> },
              // Step four: the shape stands, now the grating goes on it. Reached from
              // the schema screen, and from either way in — a drawing that was read
              // and one that was drawn arrive here with the same thing in hand.
              { path: "planung", element: demoOnly(<PlanningRoute />) },
              // The same board, opened on a Vorgang that already exists on the
              // server (task 15) — the register demo's `/planung` above stays a
              // separate, stateless path rather than growing an optional param.
              { path: "vorgang/:caseId/planung", element: <CaseRoute /> },
              // Step five without a Vorgang: nothing to price — the screen says so
              // and points at the start (design 2026-09-13 §9).
              { path: "kalkulation", element: <CalculationEntry /> },
              // Step five on a Vorgang: prices for a stored plan. Reads on open,
              // never writes (D-22) — the first calculation is a button there.
              { path: "vorgang/:caseId/kalkulation", element: <CalculationRoute /> },
              // The demonstration path: one real case through the whole chain, against
              // the local demo entrypoint. Not part of the numbered E1 sequence.
              { path: "demo", element: demoOnly(<DemoScreen />) },
              // The pattern page (GAP-39): every block in every state. Development
              // builds only — `import.meta.env.DEV` is a literal `false` in a
              // production build, so the whole branch, the `lazy` import with it,
              // is dropped and the screen never reaches the bundle. Two strings only
              // this branch (and the page it loads) carry — the route path and the
              // page's storage key — and the build check greps for both:
              //   cd apps/web && pnpm exec vite build >/dev/null \
              //     && ! grep -lE "_muster|k60\.muster\.bewegung" dist/assets/*.js
              ...(import.meta.env.DEV
                ? [
                    {
                      path: "_muster",
                      // Opened by its address, the page is the first thing the router
                      // has to load; without this it logs that no fallback was given.
                      // The shell around it is already standing, so nothing shows.
                      HydrateFallback: () => null,
                      lazy: async () => ({
                        Component: (await import("./features/muster/MusterScreen")).MusterScreen,
                      }),
                    },
                  ]
                : []),
              // An address that leads to no screen (Welle 0, S4) — said, with
              // the way home, instead of an empty work area under the masthead.
              { path: "*", element: <NotFoundScreen /> },
            ],
          },
        ],
      },
    ],
  },
];

export const router = crossFadePageTurns(createBrowserRouter(routes));
